import { useEffect, useState } from 'react';
import { apiGet, apiPut, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { UserRole } from '../lib/roles';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { Checkbox } from '../components/FormCheck';

interface PermissionMeta {
  permission: string;
  module: string;
  scope: 'view' | 'action';
  surfaces: ('portal' | 'app')[];
}

interface RoleInfo {
  role: UserRole;
  permissions: string[];
  isDefault: boolean;
}

const ROLE_COLUMNS = Object.values(UserRole);

/**
 * ADM-22 (ROADMAP.md §17.4 Wave 2) — the matrix admins actually asked for: edit each role's
 * permission set live instead of the old ADM-18 "code change + redeploy" path. Every toggle
 * PUTs the role's full replacement set immediately (PUT /admin/roles/:role/permissions is a
 * full-replace endpoint, not a diff) and reloads — same "no draft state, act immediately"
 * pattern UserManagement.tsx already uses for role/verification changes.
 */
export function RolesPermissionsPage() {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Optimistic checked-state for in-flight toggles, keyed by "role:permission". Without this,
  // a click on this *controlled* checkbox gets immediately overridden back to its old value by
  // React's next render (checked is still server-derived at that point) until the PUT+reload
  // round-trip lands — the checkbox visibly "un-clicks" itself for that whole window. Cleared
  // once roleInfos actually reflects the value (see the reconciliation effect below), or
  // immediately on error (nothing changed server-side, so snap back right away).
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, boolean>>({});

  const {
    data: catalog,
    loading: catalogLoading,
    error: catalogError,
  } = useApiResource(() => apiGet<PermissionMeta[]>('/permissions'), []);

  const {
    data: roleInfos,
    loading: rolesLoading,
    error: rolesError,
    reload: reloadRoles,
  } = useApiResource(() => apiGet<RoleInfo[]>('/admin/roles'), []);

  // Settle pending optimistic entries once real data confirms them — avoids clearing the
  // optimistic value before the reload actually lands (which would flicker back to stale data).
  useEffect(() => {
    if (!roleInfos || Object.keys(pendingOverrides).length === 0) return;
    setPendingOverrides((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(prev)) {
        const [role, permission] = key.split(':');
        const roleInfo = roleInfos.find((r) => r.role === role);
        const actual = roleInfo?.permissions.includes(permission) ?? false;
        if (actual === prev[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleInfos]);

  const togglePermission = async (role: UserRole, permission: string, checked: boolean) => {
    const current = roleInfos?.find((r) => r.role === role)?.permissions ?? [];
    const next = checked ? [...current, permission] : current.filter((p) => p !== permission);
    const key = `${role}:${permission}`;

    setPendingOverrides((prev) => ({ ...prev, [key]: checked }));
    setBusyKey(key);
    setActionError(null);
    try {
      await apiPut(`/admin/roles/${role}/permissions`, { permissions: next });
      reloadRoles();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Permission update failed');
      // The PUT failed — nothing changed server-side, so drop the optimistic value now
      // rather than waiting on a reload that won't arrive with a different answer.
      setPendingOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } finally {
      setBusyKey(null);
    }
  };

  // Only block on the *first* load. Every successful toggle calls reloadRoles(), which flips
  // rolesLoading back to true — gating the whole table on that flag would unmount every
  // checkbox (and blank the page) on every single click, since the per-cell busyKey disable
  // is what's meant to show in-flight state, not a full-page reload flash.
  const loading = (!catalog && catalogLoading) || (!roleInfos && rolesLoading);
  const error = catalogError || rolesError;

  const modules = Array.from(new Set((catalog ?? []).map((p) => p.module)));

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Roles & Permissions</h1>
          <p>
            Edit what each role can do, live — no code change or redeploy. Client-side gating in
            this portal (and in the app) is a UX nicety only; the backend's guards enforce this
            for real on every request.
          </p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : (
        modules.map((moduleName) => {
          const modulePermissions = (catalog ?? []).filter((p) => p.module === moduleName);
          return (
            <div key={moduleName} style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{moduleName}</h2>
              <div className="dt-matrix">
              <DataTable
                id={`roles-permissions-${moduleName}`}
                rows={modulePermissions}
                getRowKey={(perm) => perm.permission}
                empty="No permissions in this module."
                noun="permissions"
                /* A permission grid is read by scanning, not by paging — every row in a
                   module is meant to be visible at once. Search still helps on the larger
                   modules, so the toolbar stays. */
                pageSize={null}
                columns={[
                  { key: 'permission', header: 'Permission', value: (perm) => perm.permission },
                  {
                    key: 'scope',
                    header: 'Scope',
                    value: (perm) => perm.scope,
                    filter: 'select',
                    render: (perm) => (
                      <StatusBadge label={perm.scope} tone={perm.scope === 'action' ? 'warn' : 'neutral'} />
                    ),
                  },
                  ...ROLE_COLUMNS.map((role) => ({
                    key: role,
                    header: role,
                    align: 'center' as const,
                    render: (perm: PermissionMeta) => {
                      const roleInfo = roleInfos?.find((r) => r.role === role);
                      const key = `${role}:${perm.permission}`;
                      const serverChecked = roleInfo?.permissions.includes(perm.permission) ?? false;
                      const checked = key in pendingOverrides ? pendingOverrides[key]! : serverChecked;
                      return (
                        <Checkbox
                          circle
                          checked={checked}
                          disabled={busyKey === key}
                          ariaLabel={`${role}: ${perm.permission}`}
                          onChange={(next) => void togglePermission(role, perm.permission, next)}
                        />
                      );
                    },
                  })),
                ]}
              />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
